'use client';

import { useCallback, useRef, useState } from 'react';

type PdfJsModule = {
  getDocument: (options: {
    data?: Uint8Array;
    disableWorker?: boolean;
    useWorkerFetch?: boolean;
    isEvalSupported?: boolean;
  }) => { promise: Promise<PdfDocument> };
  GlobalWorkerOptions: { workerSrc: string };
  version: string;
};

type PdfDocument = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfPage>;
};

type PdfPage = {
  getViewport: (options: { scale: number }) => { width: number; height: number };
  render: (options: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }) => { promise: Promise<void> };
};

const MAX_PDF_PAGES = 10;
const RENDER_SCALE = 2;
const JPEG_QUALITY = 0.85;
const MAX_DIMENSION = 1280;

export interface UsePdfImportReturn {
  importPdf: () => void;
  isImporting: boolean;
  importError: string | null;
  importProgress: string | null;
}

export function usePdfImport(
  onImagesReady: (base64Images: string[]) => void,
): UsePdfImportReturn {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<string | null>(null);

  const importPdf = useCallback(() => {
    if (isImporting) return;

    const existing = inputRef.current;
    if (existing) {
      existing.remove();
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,.pdf';
    input.style.display = 'none';
    input.multiple = false;

    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) {
        input.remove();
        return;
      }

      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        setImportError('Please select a PDF file');
        input.remove();
        return;
      }

      setIsImporting(true);
      setImportError(null);
      setImportProgress('Reading PDF...');

      try {
        const arrayBuffer = await file.arrayBuffer();
        const uint8 = new Uint8Array(arrayBuffer);

        const pdfjsModule = await import('pdfjs-dist').catch(() =>
          import('pdfjs-dist/legacy/build/pdf.mjs'),
        );
        const pdfjsLib = (pdfjsModule && 'default' in pdfjsModule
          ? pdfjsModule.default
          : pdfjsModule) as unknown as PdfJsModule;

        if (!pdfjsLib || typeof pdfjsLib.getDocument !== 'function') {
          throw new Error('Failed to load PDF viewer');
        }

        if (pdfjsLib.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
          const base = typeof window !== 'undefined' ? window.location.origin : '';
          pdfjsLib.GlobalWorkerOptions.workerSrc = `${base}/pdf.worker.min.mjs`;
        }

        const loadingTask = pdfjsLib.getDocument({
          data: uint8,
          disableWorker: true,
          useWorkerFetch: false,
          isEvalSupported: false,
        });

        const pdf = await loadingTask.promise;
        const pageCount = Math.min(pdf.numPages, MAX_PDF_PAGES);
        const images: string[] = [];

        for (let i = 1; i <= pageCount; i++) {
          setImportProgress(`Rendering page ${i} of ${pageCount}...`);
          const page = await pdf.getPage(i);

          let scale = RENDER_SCALE;
          const baseViewport = page.getViewport({ scale: 1 });
          if (baseViewport.width * scale > MAX_DIMENSION || baseViewport.height * scale > MAX_DIMENSION) {
            scale = Math.min(MAX_DIMENSION / baseViewport.width, MAX_DIMENSION / baseViewport.height);
          }

          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas context unavailable');

          await page.render({ canvasContext: ctx, viewport }).promise;
          const base64 = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
          images.push(base64);

          canvas.width = 0;
          canvas.height = 0;
        }

        setImportProgress(null);
        if (images.length > 0) {
          onImagesReady(images);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to import PDF';
        setImportError(message);
      } finally {
        setIsImporting(false);
        setImportProgress(null);
        input.remove();
      }
    });

    inputRef.current = input;
    document.body.appendChild(input);
    input.click();
  }, [isImporting, onImagesReady]);

  return {
    importPdf,
    isImporting,
    importError,
    importProgress,
  };
}
